import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const AuthCallback = () => {
  const { loginWithGoogle } = useAuth();
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const hasProcessed = useRef(false);
  const sessionIdRef = useRef(null);

  const exchangeSession = async (sessionId) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await axios.post(
      `${API_URL}/api/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );

    // Store auth data in localStorage
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));

    // Determine redirect path
    const role = userData?.role;
    let redirectPath = '/dashboard';
    if (role === 'admin') redirectPath = '/admin';
    else if (role === 'driver') redirectPath = '/driver';

    // Full page navigation - clears hash and avoids any React state issues
    window.location.replace(redirectPath);
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      goToLogin();
      return;
    }

    sessionIdRef.current = sessionId;
    
    const processAuth = async () => {
      try {
        await exchangeSession(sessionId);
      } catch (err) {
        // Auto-retry twice with shorter delays
        for (let i = 0; i < 2; i++) {
          await new Promise(r => setTimeout(r, 800));
          try {
            await exchangeSession(sessionId);
            return; // Success
          } catch (retryErr) {
            if (i === 1) {
              const detail = retryErr.response?.data?.detail || 'Authentication failed';
              setError(detail);
            }
          }
        }
      }
    };
    processAuth();
  }, []);

  const goToLogin = () => {
    // Use window.location.replace to fully clear the hash and navigate cleanly
    window.location.replace('/login');
  };

  const handleRetry = async () => {
    if (!sessionIdRef.current) {
      goToLogin();
      return;
    }
    setRetrying(true);
    setError(null);
    try {
      await exchangeSession(sessionIdRef.current);
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="text-center max-w-sm px-6">
        {error ? (
          <>
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <p className="text-red-600 font-medium text-lg mb-2">Sign-in Issue</p>
            <p className="text-zinc-500 text-sm mb-5">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                disabled={retrying}
                data-testid="retry-auth-btn"
              >
                {retrying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Retry
              </Button>
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={goToLogin}
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Signing you in with Google...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
